import { ArrowLeft, ArrowRight, Check, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { WidgetField, WidgetFieldSource } from "@/domains/buildings/types/buildingWidget";
import {
  fieldHasSourceEvidence,
  type FieldEvidence,
  type useFieldEvidence,
} from "@/hooks/buildings/useFieldEvidence";
import type { BuildingDataWidgetSections } from "@/lib/i18n/types/buildings-sections/building-data-widget";
import type { ReviewQueueItem } from "./BuildingDataReviewQueue";
import { EvidenceSourceCard } from "./EvidenceSourceCard";
import { SourceGroupChip } from "./SourceGroupChip";
import { alternateCandidates, type ValueOption } from "./sourceGroups";

type MachineSource = Exclude<WidgetFieldSource, { kind: "user" }>;

function confidenceLevel(
  item: ReviewQueueItem,
  selectedValue: string,
  tw: BuildingDataWidgetSections
) {
  const normalizedValue = selectedValue.trim().toLocaleLowerCase();
  const source = item.field.sources.find(
    (candidate): candidate is MachineSource =>
      candidate.kind !== "user" && candidate.value.trim().toLocaleLowerCase() === normalizedValue
  );
  if (!source) return { label: "—", level: 0 };
  if (source.kind === "ehr" || source.confidence === "high") {
    return { label: tw.confidenceHigh, level: 3 };
  }
  if (source.confidence === "medium") return { label: tw.confidenceMedium, level: 2 };
  if (source.confidence === "low") return { label: tw.confidenceLow, level: 1 };
  return { label: "—", level: 0 };
}

function ReviewHeader(props: {
  item: ReviewQueueItem;
  index: number;
  total: number;
  tw: BuildingDataWidgetSections;
  onClose: () => void;
}) {
  const progress = props.tw.reviewProgress
    .replace("{current}", String(props.index + 1))
    .replace("{total}", String(props.total));
  return (
    <header className="flex items-start gap-4 border-b border-border px-6 py-5">
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-primary">
          {progress}
        </div>
        <h2 className="mt-1 text-xl font-semibold text-foreground">{props.item.field.label}</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">{props.item.groupTitle}</p>
      </div>
      <button
        type="button"
        onClick={props.onClose}
        className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
        aria-label={props.tw.exitReview}
      >
        <X size={17} />
      </button>
    </header>
  );
}

function ReviewProgressBar(props: { index: number; total: number }) {
  const percent = props.total > 0 ? ((props.index + 1) / props.total) * 100 : 0;
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={props.total}
      aria-valuenow={Math.min(props.index + 1, props.total)}
      className="h-1 w-full shrink-0 bg-muted"
    >
      <div
        className="h-full bg-primary transition-[width] duration-300"
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}

function reviewPageLabel(evidence: FieldEvidence, tw: BuildingDataWidgetSections): string | null {
  if (evidence.sheetName) return evidence.sheetName;
  if (evidence.pageNumber !== null && evidence.pageNumber !== undefined) {
    return `${tw.pageLabel} ${evidence.pageNumber}`;
  }
  return null;
}

function ReviewEvidence(props: {
  field: WidgetField;
  value: string;
  evidence: FieldEvidence | undefined;
  isLoading: boolean;
  isError: boolean;
  registryCode: string | null;
  tw: BuildingDataWidgetSections;
  onRetry: () => void;
  onDocPreview: (filename: string) => void;
}) {
  const { evidence, isLoading, isError, registryCode, tw, onRetry, onDocPreview } = props;
  // No source reported the selected value: the section would be all placeholder
  // copy — drop it. Decided from the field so it holds while the query is in
  // flight; gating on the settled result showed the heading and its spinner for
  // the length of the fetch, then removed both.
  if (!fieldHasSourceEvidence(props.field, props.value)) return null;
  return (
    <>
      <div className="mt-5 text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
        {tw.sourceEvidence}
      </div>
      {isLoading ? (
        <span className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 size={14} className="animate-spin" />
          {tw.loadingEvidence}
        </span>
      ) : isError ? (
        <span
          role="alert"
          className="mt-2 flex items-center justify-between gap-3 text-sm text-destructive"
        >
          <span>{tw.evidenceLoadError}</span>
          <Button variant="outline" size="sm" className="h-7 shrink-0" onClick={onRetry}>
            {tw.retryEvidence}
          </Button>
        </span>
      ) : evidence ? (
        <EvidenceSourceCard
          isEhr={evidence.source?.kind === "ehr"}
          filename={evidence.filename}
          canPreview={evidence.canPreview}
          pageLabel={reviewPageLabel(evidence, tw)}
          reasoning={evidence.passage}
          registryCode={registryCode}
          onDocPreview={onDocPreview}
        />
      ) : null}
    </>
  );
}

function ProposedValue(props: {
  item: ReviewQueueItem;
  value: string;
  tw: BuildingDataWidgetSections;
}) {
  return (
    <>
      <div className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
        {props.tw.proposedValue}
      </div>
      <div className="mt-2 rounded-lg border border-warning/50 bg-warning/10 px-4 py-3 text-lg font-semibold text-foreground [font-variant-numeric:tabular-nums]">
        {props.value}
        {props.item.field.unit ? (
          <span className="ml-1.5 text-sm font-normal text-muted-foreground">
            {props.item.field.unit}
          </span>
        ) : null}
      </div>
    </>
  );
}

function AlternateCandidateCard(props: {
  candidate: ValueOption;
  registryCode: string | null;
  onSelect: (value: string) => void;
  onDocPreview: (filename: string) => void;
}) {
  const { candidate } = props;
  // The whole card can't be one button any more: the source chips inside it are
  // buttons and popover triggers themselves. So the selecting surface is the
  // value row stretched across the card, padding included, and the hover tint
  // lives there — the chip row never lights up promising a selection it cannot
  // make.
  return (
    <div className="rounded-md border border-border">
      <button
        type="button"
        onClick={() => props.onSelect(candidate.value)}
        className="block w-full cursor-pointer rounded-t-md px-3 pb-1 pt-2 text-left text-sm text-foreground transition-colors hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        {candidate.value}
      </button>
      <div className="flex flex-wrap gap-1 px-3 pb-2 pt-0.5">
        {candidate.groups.map((group, index) => (
          <SourceGroupChip
            key={`${group.kind}-${index}`}
            group={group}
            registryCode={props.registryCode}
            onDocPreview={props.onDocPreview}
          />
        ))}
      </div>
    </div>
  );
}

function ReviewAside(props: {
  confidence: { label: string; level: number };
  alternates: ValueOption[];
  registryCode: string | null;
  tw: BuildingDataWidgetSections;
  onSelect: (value: string) => void;
  onDocPreview: (filename: string) => void;
}) {
  return (
    <aside className="space-y-5">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
          {props.tw.confidence}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <div className="flex gap-1">
            {[1, 2, 3].map((bar) => (
              <span
                key={bar}
                className={`h-2.5 w-9 rounded-sm ${bar <= props.confidence.level ? "bg-success" : "bg-muted/50"}`}
              />
            ))}
          </div>
          <span className="text-xs font-semibold text-muted-foreground">
            {props.confidence.label}
          </span>
        </div>
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
          {props.tw.alternateCandidates}
        </div>
        {props.alternates.length > 0 ? (
          <div className="mt-2 space-y-2">
            {props.alternates.map((candidate) => (
              <AlternateCandidateCard
                key={candidate.value}
                candidate={candidate}
                registryCode={props.registryCode}
                onSelect={props.onSelect}
                onDocPreview={props.onDocPreview}
              />
            ))}
          </div>
        ) : (
          <p className="mt-2 text-xs text-muted-foreground">{props.tw.noAlternateCandidates}</p>
        )}
      </div>
    </aside>
  );
}

function ReviewBody(props: {
  item: ReviewQueueItem;
  value: string;
  evidence: FieldEvidence | undefined;
  isLoading: boolean;
  isError: boolean;
  registryCode: string | null;
  tw: BuildingDataWidgetSections;
  onRetry: () => void;
  onDocPreview: (filename: string) => void;
  onSelectValue: (value: string) => void;
}) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
      <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_220px]">
        <section>
          <ProposedValue item={props.item} value={props.value} tw={props.tw} />
          <ReviewEvidence
            field={props.item.field}
            value={props.value}
            evidence={props.evidence}
            isLoading={props.isLoading}
            isError={props.isError}
            registryCode={props.registryCode}
            tw={props.tw}
            onRetry={props.onRetry}
            onDocPreview={props.onDocPreview}
          />
        </section>
        <ReviewAside
          confidence={confidenceLevel(props.item, props.value, props.tw)}
          alternates={alternateCandidates(props.item.field.sources, props.value)}
          registryCode={props.registryCode}
          tw={props.tw}
          onSelect={props.onSelectValue}
          onDocPreview={props.onDocPreview}
        />
      </div>
    </div>
  );
}

function ReviewValidationWarning(props: {
  value: string | null;
  isSaving: boolean;
  tw: BuildingDataWidgetSections;
  onEdit: () => void;
  onSaveAnyway: () => void;
}) {
  if (props.value === null) return null;
  return (
    <div role="alert" className="mx-6 mb-4 rounded-md border border-warning/50 bg-warning/10 p-3">
      <div className="text-xs font-semibold text-foreground">{props.tw.extremeValueTitle}</div>
      <div className="mt-1 text-xs leading-5 text-foreground">{props.tw.extremeValueBody}</div>
      <div className="mt-2 flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-7"
          disabled={props.isSaving}
          onClick={props.onEdit}
        >
          {props.tw.extremeValueEdit}
        </Button>
        <Button size="sm" className="h-7" disabled={props.isSaving} onClick={props.onSaveAnyway}>
          {props.tw.extremeValueSaveAnyway}
        </Button>
      </div>
    </div>
  );
}

function ReviewFooter(props: {
  isSaving: boolean;
  tw: BuildingDataWidgetSections;
  onPrevious: () => void;
  onNext: () => void;
  onReject: () => void;
  onAccept: () => void;
}) {
  return (
    <footer className="flex flex-wrap items-center gap-2 border-t border-border bg-muted/15 px-6 py-4">
      <Button variant="outline" onClick={props.onPrevious} className="h-9">
        <ArrowLeft size={14} />
        {props.tw.previous}
      </Button>
      <Button variant="outline" onClick={props.onNext} className="h-9">
        <ArrowRight size={14} />
        {props.tw.skip}
      </Button>
      <div className="ml-auto flex gap-2">
        <Button
          variant="outline"
          onClick={props.onReject}
          disabled={props.isSaving}
          className="h-9 border-destructive/30 text-destructive hover:bg-destructive/10"
        >
          <X size={14} />
          {props.tw.reject}
        </Button>
        <Button onClick={props.onAccept} disabled={props.isSaving} className="h-9">
          {props.isSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          {props.tw.accept}
        </Button>
      </div>
    </footer>
  );
}

interface BuildingDataReviewDialogProps {
  item: ReviewQueueItem;
  value: string;
  index: number;
  total: number;
  evidence: ReturnType<typeof useFieldEvidence>;
  isSaving: boolean;
  registryCode: string | null;
  tw: BuildingDataWidgetSections;
  onClose: () => void;
  onDocPreview: (filename: string) => void;
  onPrevious: () => void;
  onNext: () => void;
  onReject: () => void;
  onAccept: () => void;
  onSelectValue: (value: string) => void;
  pendingExtremeValue: string | null;
  onEditExtremeValue: () => void;
  onSaveExtremeValue: () => void;
}

export function BuildingDataReviewDialog(props: BuildingDataReviewDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/35 p-6 backdrop-blur-[2px]">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={props.tw.reviewQueue}
        className="flex max-h-[min(760px,calc(100vh-48px))] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-border bg-background shadow-2xl"
      >
        <ReviewHeader
          item={props.item}
          index={props.index}
          total={props.total}
          tw={props.tw}
          onClose={props.onClose}
        />
        <ReviewProgressBar index={props.index} total={props.total} />
        <ReviewBody
          item={props.item}
          value={props.value}
          evidence={props.evidence.data}
          isLoading={props.evidence.isLoading}
          isError={props.evidence.isError}
          registryCode={props.registryCode}
          tw={props.tw}
          onRetry={() => void props.evidence.refetch()}
          onDocPreview={props.onDocPreview}
          onSelectValue={props.onSelectValue}
        />
        <ReviewValidationWarning
          value={props.pendingExtremeValue}
          isSaving={props.isSaving}
          tw={props.tw}
          onEdit={props.onEditExtremeValue}
          onSaveAnyway={props.onSaveExtremeValue}
        />
        <ReviewFooter
          isSaving={props.isSaving}
          tw={props.tw}
          onPrevious={props.onPrevious}
          onNext={props.onNext}
          onReject={props.onReject}
          onAccept={props.onAccept}
        />
      </div>
    </div>
  );
}
